import { FormikProps, FormikValues } from 'formik';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import isEmpty from 'lodash/isEmpty';

import { Button } from 'shared/components/Button';
import { ConsentModal } from 'checkout/components/Tabs/ConsentModal';
import { ExpressPayModal } from 'checkout/components/Payment/ExpressPayModal';
import { ITabName } from 'checkout/types/Tab';
import { Modal } from 'shared/components/Modal';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { ShippingAddresses } from 'checkout/components/Shipping/ShippingAddresses';
import { ShippingAddressForm } from 'checkout/components/Shipping/ShippingAddressForm';
import { ShippingMethods } from 'checkout/components/Shipping/ShippingMethods';
import { TabPanelWrapper } from 'checkout/components/Tabs/Components';
import { useCheckoutContext } from 'checkout/providers/CheckoutContextProvider';
import { useCurrency } from 'shared/hooks/useCurrency';
import { CHINA_COUNTRY_CODE } from 'checkout/enums/address';
import { GiftCardEventTypes } from 'gift-cards/enums/constants';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { t } from 'localization/utils/t';

const TP = 'checkout.components.Tabs.ShippingTab';

export const ShippingTab = () => {
  const formRef = useRef<FormikProps<FormikValues>>(null);

  const { selectedCurrencyCode } = useCurrency();
  const { user } = useStoreSelector((state) => state.auth);
  const {
    addresses,
    changeTab,
    dismissInternationalFeeWarning,
    displayInternationalFeeWarning,
    loading,
    order,
  } = useCheckoutContext();

  const [consentModal, setConsentModal] = useState({ display: false, consent: false });
  const [isAddingAddress, setIsAddingAddress] = useState(false);

  const shippingAddresses = Object.values(addresses).filter(
    (address) => address.addressType === 'shipping',
  );

  const isGuestAndHasAddress = user?.isGuest && !isEmpty(shippingAddresses);
  let giftCardType = GiftCardEventTypes.NULL;
  if (order?.product?.giftCardId) {
    giftCardType = GiftCardEventTypes.PURCHASED;
  }
  if (order?.giftCards?.length > 0) {
    giftCardType = GiftCardEventTypes.APPLIED;
  }

  const handleContinueToPayment = () => {
    changeTab(ITabName.PAYMENT);

    sendTrackingEvent('CHECKOUT_ADDRESS_CONFIRMATION_TAP', {
      address_id: order.addressId,
      chinese_citizen: (addresses[order.addressId]?.countryCode === CHINA_COUNTRY_CODE).toString(),
      country: addresses[order.addressId]?.countryCode,
      event_context: 'shipping_address_form',
      shipping_method: order.shippingServiceLevel,
      state: addresses[order.addressId]?.stateCode,
    });
  };

  const handleDismissInternationalFeeWarning = () => {
    dismissInternationalFeeWarning();
    sendTrackingEvent('PRICE_UPDATE_POPUP_TAP');
  };

  const handleDismissConsentModal = (event) => {
    event.preventDefault();
    setConsentModal({ display: false, consent: false });
  };

  useEffect(() => {
    sendTrackingEvent('CHECKOUT_VIEW', {
      box_condition: order?.product?.boxCondition,
      condition_category: order?.product?.shoeCondition,
      currency_code: selectedCurrencyCode,
      instant_ship: order?.isInstantShip,
      order_number: order?.number,
      price: order?.localizedPriceCents?.amount,
      product_template_slug: order?.product?.productTemplate?.slug,
      shoe_condition: order?.product?.shoeCondition,
      size: order?.product?.size,
      step: 'shipping',
      gift_card_type: giftCardType,
    });
  }, [order?.id]);

  useEffect(() => {
    if (displayInternationalFeeWarning) {
      sendTrackingEvent('MODAL_OPEN_VIEW', {
        modal_name: 'price_update_modal',
      });
    }
  }, [displayInternationalFeeWarning]);

  return (
    <TabPanelWrapper>
      <ExpressPayModal />

      {isEmpty(shippingAddresses) || isAddingAddress ? (
        <ShippingAddressForm
          formRef={formRef}
          setIsAddingAddress={setIsAddingAddress}
          consentModal={consentModal}
          setConsentModal={setConsentModal}
        />
      ) : (
        <>
          <ShippingAddresses />
          {!isGuestAndHasAddress && (
            <ShippingTab.AddTextWrapper onClick={() => setIsAddingAddress(true)}>
              <ShippingTab.AddText data-qa="add_new_shipping">
                {t(`${TP}.addNewAddressLabel`, '+ Add new shipping address')}
              </ShippingTab.AddText>
            </ShippingTab.AddTextWrapper>
          )}
          <ShippingMethods />
          <ShippingTab.Button
            data-qa="checkout_shipping_submit"
            title={t(`${TP}.continueToPaymentButton`, 'Continue to Payment')}
            disabled={!order.addressId}
            loading={loading?.address}
            onClick={handleContinueToPayment}
          />
        </>
      )}

      {displayInternationalFeeWarning && (
        <ShippingTab.Modal
          data-qa="checkout_country_duties_modal"
          onClose={dismissInternationalFeeWarning}
          title={t(`${TP}.priceUpdateModalTitle`, 'Price update')}
        >
          <ShippingTab.ModalText>
            {t(
              `${TP}.landedCostChanged`,
              'The price of your item has changed. Pricing can fluctuate based on shipping destination or availability. Please review the updated cost prior to check out.',
            )}
          </ShippingTab.ModalText>
          <ShippingTab.ModalButton
            data-qa="checkout_country_duties_modal_confirm"
            onClick={handleDismissInternationalFeeWarning}
          >
            {t(`${TP}.shippingConfirmButton`, 'Okay')}
          </ShippingTab.ModalButton>
        </ShippingTab.Modal>
      )}

      {consentModal.display && (
        <ConsentModal
          onOkClick={() => {
            setConsentModal({ display: false, consent: true });
            void formRef.current.submitForm();
          }}
          onCancelClick={handleDismissConsentModal}
          onClose={() => {
            setConsentModal({ display: false, consent: false });
          }}
        />
      )}
    </TabPanelWrapper>
  );
};

ShippingTab.Button = styled(Button)`
  font-size: 11px;
  width: 100%;
  text-transform: uppercase;
  margin-top: 48px;

  @media (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    max-width: 428px;
    font-size: 11.5px;
  }
`;

ShippingTab.AddTextWrapper = styled.p`
  cursor: pointer;
  padding-top: 44px;
  margin: 0;
`;

ShippingTab.AddText = styled.span`
  text-decoration: underline;
`;

ShippingTab.Modal = styled(Modal)`
  max-width: 335px;
`;

ShippingTab.ModalText = styled.p`
  margin: 0;
  padding: 8px 0px 32px 0px;
`;

ShippingTab.ModalButton = styled(Button)`
  font-size: 11px;
  width: 100%;
  height: 32px;
  text-transform: uppercase;
`;
